/**
 * Sprint 24.15.2 — Connections hiring banner.
 *
 * Rendered at the top of `/connections` when the page is reached from a hire's
 * "Open Connections" flow (`?missing=k1,k2&return_to=…`). Surfaces which
 * connections the in-progress hire needs, with a per-kind install CTA for the
 * installable ones and a roadmap pill for coming-soon kinds, plus an escape hatch
 * back to the hire. Presentational — the page owns install routing + auto-resume.
 */

import type { CSSProperties } from "react";
import type { AvailabilityStatus } from "../../hooks/use-agent-templates";
import { EquipmentStatusDot, equipmentStatusMeta } from "../hire-wizard/template-visuals";

export interface HiringBannerMissing {
  kind: string;
  status: AvailabilityStatus;
  note?: string | null;
  /** True once an installed connection satisfies this kind (auto-resume gate). */
  installed: boolean;
}

export interface HiringBannerProps {
  templateName: string;
  missing: HiringBannerMissing[];
  onInstallKind: (kind: string, status: AvailabilityStatus) => void;
  onReturn: () => void;
}

export function HiringBanner({ templateName, missing, onInstallKind, onReturn }: HiringBannerProps) {
  const n = missing.length;
  return (
    <div style={banner}>
      <i className="ti ti-user-plus" style={{ fontSize: 16, color: "var(--color-text-info)", flexShrink: 0, marginTop: 1 }} aria-hidden="true" />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={title}>Hiring {templateName}</div>
        <div style={bodyText}>
          Install {n === 1 ? "this connection" : `these ${n} connections`} to continue. You can also continue without them.
        </div>

        <div style={list}>
          {missing.map((m) => {
            const meta = equipmentStatusMeta(m.status, m.note, m.kind);
            return (
              <div key={m.kind} style={row}>
                <EquipmentStatusDot color={meta.dotColor} size={8} />
                <span style={kindText}>{m.kind}</span>
                {m.installed ? (
                  <span style={installedLabel}>
                    <i className="ti ti-check" style={{ fontSize: 12 }} aria-hidden="true" /> Installed
                  </span>
                ) : meta.installable && meta.ctaLabel ? (
                  <button type="button" style={ctaBtn} onClick={() => onInstallKind(m.kind, m.status)}>
                    {meta.ctaLabel}
                  </button>
                ) : (
                  <span style={comingSoonLabel}>{meta.statusLabel}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <button type="button" style={returnBtn} onClick={onReturn}>
        ← Return to hire without installing
      </button>
    </div>
  );
}

const banner: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: 12,
  padding: "12px 16px",
  marginBottom: 16,
  borderRadius: "var(--border-radius-md)",
  background: "var(--color-background-info)",
  border: "0.5px solid var(--color-border-info)",
};
const title: CSSProperties = { fontSize: 14, fontWeight: 500, color: "var(--color-text-info)" };
const bodyText: CSSProperties = { fontSize: 13, color: "var(--color-text-info)", marginTop: 2, opacity: 0.9 };
const list: CSSProperties = { display: "flex", flexDirection: "column", gap: 6, marginTop: 10 };
const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "6px 10px",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
};
const kindText: CSSProperties = { flex: 1, fontSize: 13, color: "var(--color-text-primary)" };
const ctaBtn: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  padding: "2px 10px",
  borderRadius: "var(--border-radius-sm)",
  border: "0.5px solid var(--color-border-info)",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};
const comingSoonLabel: CSSProperties = { fontSize: 12, color: "var(--color-text-warning)", whiteSpace: "nowrap" };
const installedLabel: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  fontSize: 12,
  color: "var(--color-text-success)",
  whiteSpace: "nowrap",
};
const returnBtn: CSSProperties = {
  flexShrink: 0,
  alignSelf: "flex-start",
  fontSize: 12,
  fontWeight: 500,
  padding: "4px 4px",
  border: "none",
  background: "transparent",
  color: "var(--color-text-info)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
  whiteSpace: "nowrap",
};
